import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, RefreshCw } from 'lucide-react';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';

// BILLS (owner, 2026-09-02) — the Super Admin's read of the unified billing
// surface the tenant app already has. Read-only: collecting money is the
// hospital's desk, not the platform's.
//
// EVERY HOSPITAL, IN ONE LIST, and the copy says so. This used to be one
// hospital's bills because the patientId handed in was that hospital's own
// patients row. Since one-patient-row (owner, 22 Sep 2026) the id is the
// PERSON, GET /billing/patient/:patientId takes no tenant, and a super_admin
// token is unscoped — so what comes back is every invoice raised for this
// person anywhere. Naming one hospital above this table would have been a
// filter the request never applied. (The rows carry no tenant of their own
// yet, so they cannot be grouped by hospital here either — that needs the
// endpoint to send it.)

const money = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? v.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-';
};

const STATUS_TONES = {
    paid: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    partial: 'bg-amber-50 text-amber-600 border-amber-200',
    unpaid: 'bg-rose-50 text-rose-600 border-rose-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function MocBillsTab({ patientId }) {
    const { t } = useTranslation();
    const axiosInstance = useAxiosAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!patientId) { setData(null); return undefined; }
        let alive = true;
        setLoading(true);
        setError('');
        (async () => {
            try {
                const res = await axiosInstance.get(`${baseURL}/billing/patient/${patientId}`);
                if (alive) setData(res?.data || null);
            } catch (err) {
                if (alive) setError(err?.response?.data?.message || t('moc.bills_failed', 'Could not load the bills'));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, tick]);

    const summary = data?.summary || {};
    const invoices = data?.invoices || [];

    return (
        <div data-testid="moc-bills-tab">
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500" data-testid="moc-bills-scope">
                    {t('moc.bills_scope', 'Every bill raised for this patient, at every hospital')}
                </p>
                <button
                    type="button"
                    onClick={() => setTick((k) => k + 1)}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
                    title={t('common.refresh', 'Refresh')}
                    data-testid="moc-bills-refresh"
                >
                    <RefreshCw size={15} />
                </button>
            </div>

            {loading ? (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse h-16 bg-gray-50 rounded-2xl" />)}
                    </div>
                    <div className="animate-pulse h-40 bg-gray-50 rounded-2xl" />
                </div>
            ) : error ? (
                <p data-testid="moc-bills-error" className="text-sm text-rose-600">{error}</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
                            <p className="text-[11px] text-gray-400 uppercase tracking-wide">{t('moc.bills_count', 'Bills')}</p>
                            <p className="text-lg font-bold text-gray-800">{data?.count ?? 0}</p>
                        </div>
                        <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
                            <p className="text-[11px] text-gray-400 uppercase tracking-wide">{t('moc.bills_outstanding', 'Outstanding')}</p>
                            <p className={`text-lg font-bold ${Number(data?.outstanding) > 0 ? 'text-rose-600' : 'text-gray-800'}`}>₹{money(data?.outstanding || 0)}</p>
                        </div>
                        {Object.entries(summary).slice(0, 2).map(([source, s]) => (
                            <div key={source} className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
                                <p className="text-[11px] text-gray-400 uppercase tracking-wide capitalize">{source}</p>
                                <p className="text-sm font-bold text-gray-800">₹{money(s.paid)} <span className="text-[11px] font-medium text-gray-400">/ ₹{money(s.billed)}</span></p>
                            </div>
                        ))}
                    </div>

                    {invoices.length === 0 ? (
                        <div className="text-center py-10 text-gray-400">
                            <Receipt size={28} className="mx-auto mb-2 opacity-40" />
                            <p className="text-sm">{t('moc.bills_none', 'No bills for this patient yet')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-gray-100">
                            <table className="w-full text-left" data-testid="moc-bills-table">
                                <thead>
                                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
                                        <th className="p-3">{t('moc.bill_no', 'Invoice')}</th>
                                        <th className="p-3">{t('moc.bill_date', 'Date')}</th>
                                        <th className="p-3">{t('moc.bill_source', 'Source')}</th>
                                        <th className="p-3 text-right">{t('moc.bill_total', 'Total')}</th>
                                        <th className="p-3 text-right">{t('moc.bill_paid', 'Paid')}</th>
                                        <th className="p-3 text-right">{t('moc.bill_balance', 'Balance')}</th>
                                        <th className="p-3">{t('moc.bill_status', 'Status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv) => (
                                        <tr key={inv.invoiceId} data-testid={`moc-bill-row-${inv.invoiceId}`} className="border-t border-gray-50 text-xs text-gray-600">
                                            <td className="p-3 font-mono">{inv.invoiceNo || inv.invoiceId}</td>
                                            <td className="p-3">{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '-'}</td>
                                            <td className="p-3 capitalize">{inv.billSource || '-'}</td>
                                            <td className="p-3 text-right">₹{money(inv.grandTotal)}</td>
                                            <td className="p-3 text-right">₹{money(inv.paidTotal)}</td>
                                            <td className="p-3 text-right">₹{money(inv.balance)}</td>
                                            <td className="p-3">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold capitalize ${STATUS_TONES[inv.status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                                                    {inv.status || '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
